/**
 * Drill-through URL contract: Analytics → Activities and Explorer.
 * Params must match what Activities.tsx and Explorer.tsx parse.
 * Canonical WBS filter: wbs_id (prefer over wbs_code).
 */

const ACTIVITIES_BASE = '/app/activities';
const EXPLORER_BASE = '/app/explorer';

export interface ActivitiesDrillFilters {
  wbs_id?: string;
  wbs_code?: string;
  negativeFloat?: boolean;
  critical?: boolean;
  filter?: 'milestones';
  overdue?: boolean;
  atRisk?: boolean;
  /** Optional Phase 1: e.g. total_float_asc */
  sort?: string;
  /** Float range in days (total_float_hr_cnt / 8) */
  float_min?: number;
  float_max?: number;
  /** Single activity to show (task_id / activity id) */
  activity_id?: string;
  /** Comma-separated activity IDs to show (e.g. from exception panels) */
  activity_ids?: string[] | string;
}

/**
 * Build Activities URL with query params. Only includes params that are set
 * and that Activities page supports (client-side filtering).
 */
export function toActivitiesUrl(filters: ActivitiesDrillFilters): string {
  const params = new URLSearchParams();
  if (filters.wbs_id != null && filters.wbs_id !== '') {
    params.set('wbs_id', String(filters.wbs_id));
  }
  if (filters.wbs_code != null && filters.wbs_code !== '') {
    params.set('wbs_code', String(filters.wbs_code));
  }
  if (filters.negativeFloat === true) {
    params.set('negativeFloat', 'true');
  }
  if (filters.critical === true) {
    params.set('critical', 'true');
  }
  if (filters.filter === 'milestones') {
    params.set('filter', 'milestones');
  }
  if (filters.overdue === true) {
    params.set('overdue', 'true');
  }
  if (filters.atRisk === true) {
    params.set('atRisk', 'true');
  }
  if (filters.sort != null && filters.sort !== '') {
    params.set('sort', String(filters.sort));
  }
  if (filters.float_min != null && !Number.isNaN(filters.float_min)) {
    params.set('float_min', String(filters.float_min));
  }
  if (filters.float_max != null && !Number.isNaN(filters.float_max)) {
    params.set('float_max', String(filters.float_max));
  }
  if (filters.activity_id != null && filters.activity_id !== '') {
    params.set('activity_id', String(filters.activity_id));
  }
  if (filters.activity_ids != null) {
    const ids = Array.isArray(filters.activity_ids) ? filters.activity_ids : String(filters.activity_ids).split(',').map((s) => s.trim()).filter(Boolean);
    if (ids.length > 0) {
      params.set('activity_ids', ids.join(','));
    }
  }
  const qs = params.toString();
  return qs ? `${ACTIVITIES_BASE}?${qs}` : ACTIVITIES_BASE;
}

/**
 * Returns Explorer (Raw Data Viewer) URL. Optional ?table= preselects table.
 */
export function toExplorerUrl(table?: string): string {
  if (table != null && table !== '') {
    return `${EXPLORER_BASE}?table=${encodeURIComponent(table)}`;
  }
  return EXPLORER_BASE;
}

/** Alias for toExplorerUrl for backward compatibility */
export const toRawDataUrl = toExplorerUrl;
